// Auto sign-out after IDLE_LIMIT_MS of no activity, with a 1-minute warning first.
//
// Design (decided 2026-08-08; load-staleness removed 2026-08-08 after it caused prod 401s):
// - IN-SESSION idle timer only: logs out after 60 min of real inactivity. No load-time signOut — an earlier
//   "reopened-after-window -> signOut on load" check fired during the app's data-load and cleared the token
//   mid-flight (queries went out anon). There is no reliable at-browser-close logout on the web without
//   sessionStorage, which breaks multi-tab (rejected), so we deliberately DON'T attempt it.
// - Typing can never log you out: keydown is activity and re-arms the timer.
//
// ═══ ONE IDLE CLOCK FOR EVERY TAB (Philip, 2026-10-10 — docs/signout-investigation.md) ═══
// The clock used to be PER TAB while the sign-out is global (and every tab shares one stored session anyway): a
// Wigley tab left untouched in the background reached 60 minutes and signed the user out of the tab they were
// typing in, its warning showing only in the hidden tab. Now each tab writes its last activity to localStorage
// (LAST_KEY) and listens for the others' ('storage' event), so activity ANYWHERE counts everywhere. Before warning
// and again before signing out, a tab re-reads the shared time and re-arms for the remainder if another tab was
// used meanwhile — a timer can never act on its own tab's view alone. The warning shows in every tab; "Stay signed
// in" in any of them is activity, so it dismisses them all.
import { useState, useEffect, useRef, useCallback } from 'react';

const IDLE_LIMIT_MS = 60 * 60 * 1000;   // 60 min total until sign-out
const WARNING_MS    = 60 * 1000;        // final 60s shown as a warning
const THROTTLE_MS   = 2000;             // cap the per-event work (mousemove fires fast)
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'];
export const LAST_KEY = 'wh_lastActivity';

const readShared = () => { try { return Number(localStorage.getItem(LAST_KEY)) || 0; } catch (e) { return 0; } };
const writeShared = (t) => { try { localStorage.setItem(LAST_KEY, String(t)); } catch (e) { /* storage blocked: this tab alone */ } };

export function useIdleLogout(enabled, onLogout) {
  const [warningOpen, setWarningOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.round(WARNING_MS / 1000));
  const warnTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownTimer = useRef(null);
  const lastActivity = useRef(0);
  const savedTitle = useRef(null);

  // Keep onLogout in a ref so an inline arrow from the caller doesn't re-run the effect every render.
  const onLogoutRef = useRef(onLogout);
  useEffect(() => { onLogoutRef.current = onLogout; });

  // The latest activity in ANY tab.
  const lastAny = useCallback(() => Math.max(lastActivity.current, readShared()), []);

  const clearAll = useCallback(() => {
    clearTimeout(warnTimer.current);
    clearTimeout(logoutTimer.current);
    clearInterval(countdownTimer.current);
  }, []);

  const restoreTitle = useCallback(() => {
    if (savedTitle.current !== null) { document.title = savedTitle.current; savedTitle.current = null; }
  }, []);

  // arm(): schedule the warning for (latest activity anywhere + limit − warning). Both timers re-check the shared
  // time when they fire, so a tab never warns or signs out while another tab is in use.
  const arm = useCallback(() => {
    clearAll(); setWarningOpen(false); restoreTitle();
    const warnAt = lastAny() + IDLE_LIMIT_MS - WARNING_MS;
    warnTimer.current = setTimeout(function warn() {
      if (Date.now() < lastAny() + IDLE_LIMIT_MS - WARNING_MS - 1000) { arm(); return; }   // used elsewhere meanwhile
      const deadline = lastAny() + IDLE_LIMIT_MS;
      let s = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setWarningOpen(true); setSecondsLeft(s);
      savedTitle.current = document.title;
      document.title = `⚠ Signing out in ${s}s`;
      countdownTimer.current = setInterval(() => {
        s -= 1; setSecondsLeft(Math.max(s, 0));
        document.title = `⚠ Signing out in ${Math.max(s, 0)}s`;
      }, 1000);
      logoutTimer.current = setTimeout(() => {
        if (Date.now() < lastAny() + IDLE_LIMIT_MS - 1000) { arm(); return; }              // used elsewhere meanwhile
        clearAll(); setWarningOpen(false); restoreTitle();
        if (onLogoutRef.current) onLogoutRef.current();
      }, Math.max(0, deadline - Date.now()));
    }, Math.max(0, warnAt - Date.now()));
  }, [clearAll, restoreTitle, lastAny]);

  const noteActivity = useCallback(() => {
    const now = Date.now();
    lastActivity.current = now;
    writeShared(now);
    arm();
  }, [arm]);

  // "Stay signed in" — explicit activity, so every tab's warning closes too.
  const staySignedIn = useCallback(() => { noteActivity(); }, [noteActivity]);

  useEffect(() => {
    if (!enabled) { clearAll(); setWarningOpen(false); restoreTitle(); return undefined; }

    // ⛔ NO load-time "staleness" signOut here. It caused production 401s: a restored session fires
    // INITIAL_SESSION (not SIGNED_IN), so a page load after 60 min looked "stale" and signOut() fired
    // DURING the app's data-load — clearing the token so concurrent queries went out as anon (401).
    // Opening a tab IS activity, so the shared clock restarts from now.
    const onActivity = () => {
      if (Date.now() - lastActivity.current < THROTTLE_MS) return;   // throttle the churn
      noteActivity();   // any activity re-arms (and dismisses the warning if it's up)
    };
    // Another tab's activity: re-arm from the shared time (closes this tab's warning if it is up).
    const onStorage = (e) => { if (e.key === LAST_KEY) arm(); };

    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);
    noteActivity();   // start the clock

    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
      clearAll();
      restoreTitle();
    };
  }, [enabled, arm, noteActivity, clearAll, restoreTitle]);

  return { warningOpen, secondsLeft, staySignedIn };
}
